import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodStoreReconciliationReviewQueue,
  fetchFoodStoreReconciliationSummary,
  postFoodStoreReconciliationApply,
  postFoodStoreReconciliationApprove,
  postFoodStoreReconciliationDryRun,
  postFoodStoreReconciliationReject,
  postFoodStoreReconciliationSplit,
} from "../../../../api/foodApi";
import AdminTableContainer from "../layout/AdminTableContainer";

const FoodStoreReconciliationPage = ({ onClose }) => {
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [summaryData, queueData] = await Promise.all([
        fetchFoodStoreReconciliationSummary(),
        fetchFoodStoreReconciliationReviewQueue({ limit: 50 }),
      ]);
      setSummary(summaryData);
      setQueue(queueData.items || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load reconciliation data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDryRun = async () => {
    try {
      setRunning(true);
      setResult(await postFoodStoreReconciliationDryRun({ brand: "all" }));
    } catch (error) {
      setErrorMessage(error.message || "Dry run failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleApply = async () => {
    if (!confirmApply) {
      setErrorMessage("Confirm apply before running reconciliation.");
      return;
    }
    try {
      setRunning(true);
      setResult(await postFoodStoreReconciliationApply({ apply: true, brand: "all" }));
      await loadData();
    } catch (error) {
      setErrorMessage(error.message || "Apply failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleAction = async (matchId, action) => {
    try {
      setRunning(true);
      if (action === "approve") await postFoodStoreReconciliationApprove(matchId, {});
      if (action === "reject") await postFoodStoreReconciliationReject(matchId, {});
      if (action === "split") await postFoodStoreReconciliationSplit(matchId, {});
      await loadData();
    } catch (error) {
      setErrorMessage(error.message || `${action} failed.`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-store-reconciliation-page">
      <header className="admin-page__header">
        <div>
          <h1>Store Reconciliation</h1>
          <p className="muted">
            Match OSM supermarket POIs with chain branch locator imports. Conservative auto-match only;
            conflicts stay in the review queue.
          </p>
        </div>
        <div className="admin-page__header-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={loading}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Back
            </button>
          ) : null}
        </div>
      </header>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !summary ? <p className="muted">Loading…</p> : null}

      {summary ? (
        <section className="panel-card">
          <h2>Summary</h2>
          <div className="food-chain-locator-strategy-page__kpi-row">
            <div><span className="muted">Canonical stores</span><strong>{summary.totalCanonicalStores}</strong></div>
            <div><span className="muted">Matched OSM+chain</span><strong>{summary.matchedOsmChain}</strong></div>
            <div><span className="muted">OSM-only</span><strong>{summary.osmOnly}</strong></div>
            <div><span className="muted">Chain-only</span><strong>{summary.chainOnly}</strong></div>
            <div><span className="muted">Needs review</span><strong>{summary.needsReview}</strong></div>
            <div><span className="muted">Conflicts</span><strong>{summary.conflicts}</strong></div>
          </div>
          {summary.byBrand?.length ? (
            <AdminTableContainer>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Matched</th>
                    <th>OSM-only</th>
                    <th>Chain-only</th>
                    <th>Needs review</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byBrand.map((row) => (
                    <tr key={row.brand}>
                      <td>{row.brand}</td>
                      <td>{row.matched}</td>
                      <td>{row.osmOnly}</td>
                      <td>{row.chainOnly}</td>
                      <td>{row.needsReview}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : null}
        </section>
      ) : null}

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <button type="button" className="btn btn-secondary" disabled={running} onClick={handleDryRun}>
            Dry run
          </button>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={confirmApply} onChange={(e) => setConfirmApply(e.target.checked)} />
            Confirm apply auto-matches
          </label>
          <button type="button" className="btn btn--primary" disabled={running} onClick={handleApply}>
            Apply auto-matches
          </button>
        </div>
      </section>

      {result ? (
        <section className="panel-card">
          <h2>Last run</h2>
          <pre className="food-normalization-page__summary">{JSON.stringify(result, null, 2)}</pre>
        </section>
      ) : null}

      <section className="panel-card">
        <h2>Review queue</h2>
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>OSM</th>
                <th>Chain</th>
                <th>Distance</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.id}>
                  <td>{row.brand}</td>
                  <td>{row.osmStore?.storeName || row.osmStoreId}</td>
                  <td>{row.chainStore?.storeName || row.chainStoreId}</td>
                  <td>{row.distanceMeters != null ? `${Math.round(row.distanceMeters)} m` : "—"}</td>
                  <td>{row.matchConfidence != null ? row.matchConfidence.toFixed(2) : "—"}</td>
                  <td>{row.matchStatus}</td>
                  <td>
                    <button type="button" className="btn btn--ghost" disabled={running} onClick={() => handleAction(row.id, "approve")}>Approve</button>
                    <button type="button" className="btn btn--ghost" disabled={running} onClick={() => handleAction(row.id, "reject")}>Reject</button>
                    <button type="button" className="btn btn--ghost" disabled={running} onClick={() => handleAction(row.id, "split")}>Split</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>
    </div>
  );
};

export default FoodStoreReconciliationPage;
